import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { useOrg } from '@/contexts/OrgContext';
import { apiPost } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';

type Item = { id: string; sku: string; name: string; quantityOnHand: number };

export function InventoryPage() {
  const { t } = useTranslation();
  const { orgId, currentPeriodId } = useOrg();
  const online = useOnlineStatus();
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [adjItem, setAdjItem] = useState('');
  const [adjQty, setAdjQty] = useState('0');
  const [adjCost, setAdjCost] = useState('0.00');

  const mapDoc = useCallback(
    (id: string, data: Record<string, unknown>) => ({
      id,
      sku: String(data.sku ?? ''),
      name: String(data.name ?? ''),
      quantityOnHand: Number(data.quantityOnHand ?? 0)
    }),
    []
  );

  const {
    rows: items,
    loading,
    pageIndex,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    pageSize,
    setPageSize,
    refresh
  } = useFirestorePageQuery<Item>({
    orgId,
    collectionPath: 'inventoryItems',
    orderByField: 'name',
    orderDirection: 'asc',
    mapDoc
  });

  async function addItem() {
    if (!orgId || !online) return;
    await addDoc(collection(getFirestoreDb(), 'organizations', orgId, 'inventoryItems'), {
      sku: sku.trim(),
      name: name.trim(),
      quantityOnHand: 0,
      valuationMethod: 'average',
      createdAt: serverTimestamp()
    });
    setSku('');
    setName('');
    refresh();
  }

  async function postAdjustment() {
    if (!orgId || !currentPeriodId || !adjItem) return;
    const ref = await addDoc(collection(getFirestoreDb(), 'organizations', orgId, 'stockAdjustments'), {
      itemId: adjItem,
      quantityDelta: Number(adjQty),
      unitCost: adjCost,
      date: new Date().toISOString().slice(0, 10),
      periodId: currentPeriodId,
      status: 'draft',
      createdAt: serverTimestamp()
    });
    await apiPost('/v1/stock-adjustments/post', { orgId, adjustmentId: ref.id });
    refresh();
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('nav.inventory')}</h1>
      <div className="mt-4 flex gap-2">
        <input
          className="rounded border px-3 py-2"
          placeholder="SKU"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
        />
        <input
          className="rounded border px-3 py-2"
          placeholder={t('coa.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button onClick={() => void addItem()} disabled={!online}>
          {t('inventory.addItem')}
        </Button>
      </div>
      <PaginatedTable
        className="mt-4"
        loading={loading}
        pageIndex={pageIndex}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onNext={nextPage}
        onPrev={prevPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      >
        <table className="w-full text-sm">
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-[var(--fv-border)]">
                <td className="p-2">{it.sku}</td>
                <td className="p-2">{it.name}</td>
                <td className="p-2 text-end">{it.quantityOnHand}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PaginatedTable>
      <div className="mt-8 rounded border p-4">
        <h2 className="font-semibold">{t('inventory.adjustment')}</h2>
        <Select
          className="mt-2 w-full"
          value={adjItem}
          placeholder="—"
          onValueChange={setAdjItem}
          options={[
            { value: '', label: '—' },
            ...items.map((it) => ({ value: it.id, label: `${it.sku} — ${it.name}` }))
          ]}
        />
        <input
          className="mt-2 w-full rounded border px-3 py-2"
          placeholder={t('inventory.qtyDelta')}
          value={adjQty}
          onChange={(e) => setAdjQty(e.target.value)}
        />
        <input
          className="mt-2 w-full rounded border px-3 py-2"
          placeholder={t('inventory.unitCost')}
          value={adjCost}
          onChange={(e) => setAdjCost(e.target.value)}
        />
        <Button className="mt-2" onClick={() => void postAdjustment()} disabled={!online}>
          {t('inventory.postAdjustment')}
        </Button>
      </div>
    </div>
  );
}
